import * as React from 'react';
import { Info, CheckCircle2, TriangleAlert, XCircle, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

type AlertVariant = 'info' | 'success' | 'warning' | 'destructive';

const iconFor: Record<AlertVariant, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: TriangleAlert,
  destructive: XCircle,
};

/** The ground and the rule the panel sits on. */
const panelFor: Record<AlertVariant, string> = {
  info: 'border-primary/20 bg-primary/5',
  success: 'border-emerald-200 bg-emerald-50/70',
  warning: 'border-amber-200 bg-amber-50/70',
  destructive: 'border-destructive/20 bg-destructive/5',
};

/** With a title, the icon sits in a chip a shade deeper than the panel. */
const chipFor: Record<AlertVariant, string> = {
  info: 'bg-primary/10 text-primary',
  success: 'bg-emerald-100/70 text-emerald-600',
  warning: 'bg-amber-100/70 text-amber-600',
  destructive: 'bg-destructive/10 text-destructive',
};

/** With no chip behind it, the icon carries the colour itself. */
const inkFor: Record<AlertVariant, string> = {
  info: 'text-primary',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  destructive: 'text-destructive',
};

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  variant?: AlertVariant;
  /** The way out of the alert — a link or button, aligned to the right. */
  action?: React.ReactNode;
}

/**
 * A message about the state of the thing on screen.
 *
 * The whole panel carries the state's colour. It was a neutral card with the
 * tint confined to the icon chip; both were built and reviewed side by side
 * (Studies/Messages), and the tinted one was chosen.
 *
 * Two shapes, decided by whether there is a title:
 *
 * **With a title** it is a small panel — icon chip, heading, explanation —
 * sized to be read as a block, and the row is top-aligned so the chip lines up
 * with the heading rather than floating beside a paragraph.
 *
 * **Without one** it is a single line, so it is built like a line: no chip, a
 * smaller icon, and half the padding. Review: "when there is one line the text
 * doesn't line up with the icon… we need two versions, one with a title and
 * one without, the one without should be rather narrow" — narrow vertically,
 * clarified afterwards, so it still spans its container. A 20px line of text
 * top-aligned against a 28px chip sat visibly high in its own row; a 16px icon
 * nudged 2px down is centred on the first line exactly, which reads as centred
 * when there is one line and stays correct when the sentence wraps.
 */
export function Alert({
  className,
  variant = 'info',
  title,
  action,
  children,
  ...props
}: AlertProps) {
  const v = variant ?? 'info';
  const Icon = iconFor[v];
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start rounded-lg border text-sm',
        title ? 'gap-3 p-4' : 'gap-2.5 px-3 py-2',
        panelFor[v],
        className,
      )}
      {...props}
    >
      {title ? (
        <span
          aria-hidden
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            chipFor[v],
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      ) : (
        // 2px is exactly half the difference between the 20px line box and the
        // 16px icon, so the icon centres on the first line of the message.
        <Icon aria-hidden className={cn('mt-0.5 h-4 w-4 shrink-0', inkFor[v])} />
      )}
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium text-foreground">{title}</p>}
        {children && <div className={cn('text-muted-foreground', title && 'mt-0.5')}>{children}</div>}
      </div>
      {action && <div className="ml-auto shrink-0 self-center whitespace-nowrap">{action}</div>}
    </div>
  );
}
